import { type ComponentPropsWithoutRef, type CSSProperties, useMemo } from 'react';
import styled from 'styled-components';

import { Divider, Space } from '@/components/base';
import { RadioGroup, RadioGroupItem } from '@/components/shadcn/ui/radio-group';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import { filterMap } from '@/utils/lazy';

import type { SortOptionT } from './types';

type Props = {
  options: SortOptionT[];
  currSortStatus: NotNullableSortStatusT;
  sortAllowList?: string[];
  onChange?: (sortStatus: NotNullableSortStatusT) => void;
  style?: CSSProperties;
  useOrderBy: boolean;
};

const DescendingSortMessages = {
  string: 'Z to A',
  date: 'Latest on top',
  number: '99 to 0',
  size: 'Enterprise to Small ',
  feedbackSize: 'Most to least',
  default: 'Descending',
};

const AscendingSortMessages = {
  string: 'A to Z',
  date: 'Oldest on top',
  number: '0 to 99',
  size: 'Small to Enterprise',
  feedbackSize: 'Least to most',
  default: 'Ascending',
};

const SorterDropdown = (props: Props) => {
  const color = useColor();
  const sortingOption = useMemo(() => {
    return filterMap(props.options, (col) => {
      return {
        sortKey: col.sortKey,
        isSelected: col.sortKey === props.currSortStatus?.sortBy,
        name: col.sortName,
      };
    });
  }, [props.options, props.currSortStatus?.sortBy]);

  return (
    <Wrapper style={props.style}>
      <Typo type="body3" color={color.gray600} style={{ paddingLeft: 12 }}>
        Sort by
      </Typo>
      <Space h="8px" />
      <RadioGroup
        className="gap-1"
        value={props.currSortStatus?.sortBy}
        onValueChange={(value) => {
          props.onChange?.({
            sortBy: value,
            orderStatus: props.currSortStatus?.orderStatus || null,
          });
        }}
      >
        {sortingOption?.map((option) => (
          <RadioGroupItemLabel
            key={option.sortKey}
            htmlFor={option.sortKey}
            value={option.sortKey}
            radioId={option.sortKey}
          >
            {option.name}
          </RadioGroupItemLabel>
        ))}
      </RadioGroup>

      {props.useOrderBy && (
        <>
          <Space h="8px" />
          <Divider direction="h" backgroundColor={color.gray400} />
          <Space h="12px" />
          <Typo type="body3" color={color.gray600} style={{ paddingLeft: 12 }}>
            Order by
          </Typo>
          <Space h="8px" />
          <RadioGroup
            className="gap-1"
            value={props.currSortStatus?.orderStatus}
            onValueChange={(value) => {
              props.onChange?.({
                sortBy: props.currSortStatus.sortBy,
                orderStatus: value as OrderStatusT,
              });
            }}
          >
            <RadioGroupItemLabel htmlFor="asc" value="asc" radioId="asc">
              {
                AscendingSortMessages[
                  props.options.find(
                    (col) => col.sortKey === props.currSortStatus?.sortBy,
                  )?.orderType || 'string'
                ]
              }
            </RadioGroupItemLabel>
            <RadioGroupItemLabel htmlFor="desc" value="desc" radioId="desc">
              {
                DescendingSortMessages[
                  props.options.find(
                    (col) => col.sortKey === props.currSortStatus?.sortBy,
                  )?.orderType || 'string'
                ]
              }
            </RadioGroupItemLabel>
          </RadioGroup>
        </>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  box-sizing: border-box;
  width: 200px;
  padding: 4px;
`;

export default SorterDropdown;

const RadioGroupItemLabel = (
  props: ComponentPropsWithoutRef<'label'> & { value: string; radioId: string },
) => {
  const { value, radioId, children, ...rest } = props;
  return (
    <label
      {...rest}
      className="cursor-pointer flex items-center gap-2 px-2 py-1 hover:bg-gray-200 transition-all duration-200 rounded-md"
    >
      <RadioGroupItem value={value} id={radioId} />
      <span className="text-sm">{children}</span>
    </label>
  );
};
